import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Avatar, IconButton, Menu, MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import store from "../../store";
import WeatherLoader from "../../components/loader";
import "./_index.scss";

const injectStyles = (() => {
  let injected = false;
  return () => {
    if (injected) return;
    injected = true;
    const style = document.createElement("style");
    style.setAttribute("data-um", "true");
   style.textContent = `
      .um-search input,
      .um-search input:focus,
      .um-search input:focus-visible,
      .um-search input:active {
        outline: 0 !important;
        outline-offset: 0 !important;
        border: 0 !important;
        box-shadow: none !important;
        -webkit-appearance: none !important;
        appearance: none !important;
        all: unset !important;
        display: block !important;
        width: 100% !important;
        font-size: 13.5px !important;
        color: #fff !important;
        caret-color: #fff !important;
        background: transparent !important;
        -webkit-text-fill-color: #fff !important;
        min-width: 0 !important;
      }
      .um-search input::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
        -webkit-text-fill-color: rgba(255, 255, 255, 0.5) !important;
        opacity: 1 !important;
      }
      .um-table-wrap::-webkit-scrollbar { height: 6px; }
      .um-table-wrap::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 99px; }
      .um-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
      .um-table-wrap::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      .um-table-wrap { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
      .um-table-wrap thead th { border-left: none !important; border-right: none !important; }
      .um-table-wrap thead th::before,
      .um-table-wrap thead th::after { border-top: none !important; }
    `;
    document.head.appendChild(style);
  };
})();

interface UserData {
  client: string;
  id: number;
  name: string;
  password: string;
  email_address: string;
  project_no: string;
  operation: string;
  lat: string;
  lon: string;
  site_route: string;
  start_date: string;
  end_date: string;
  expected_date: string;
  service_types: string;
  day_shift: string;
  user_type: string;
  night_shift: string;
  last_bill_update: string;
  client_id: string;
  forecast_id: string;
}

interface OperationData {
  forecast_id: string;
  forecast_description: string;
  client_id: string;
}

const AVATAR_PALETTE = [
  "#818cf8","#38bdf8","#fbbf24","#34d399","#f87171",
  "#e879f9","#a78bfa","#2dd4bf","#fb923c","#22d3ee",
];

const ROLE_OPTIONS    = [{ value: "all", label: "All Roles" }, { value: "admin", label: "Admin" }, { value: "user", label: "User" }];
const STATUS_OPTIONS  = [{ value: "all", label: "All Status" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }];
const SHOWING_OPTIONS = [{ value: 5, label: "5" }, { value: 10, label: "10" }, { value: 25, label: "25" }];

const FilterDropdown: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}> = ({ value, options, onChange }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  const selected = options.find(o => o.value === value);
  return (
    <>
      <button className={`um-filter-btn ${open ? "open" : ""}`} onClick={e => setAnchor(e.currentTarget)}>
        <span className="um-filter-val">{selected?.label}</span>
        <KeyboardArrowDownIcon className={`um-filter-arrow ${open ? "rotated" : ""}`} />
      </button>
      <Menu anchorEl={anchor} open={open} onClose={() => setAnchor(null)}
        PaperProps={{ className: "um-menu-paper", elevation: 0 }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        {options.map(opt => (
          <MenuItem key={opt.value} disableRipple disableTouchRipple
            onClick={() => { onChange(opt.value); setAnchor(null); }}
            className={`um-menu-item ${value === opt.value ? "active" : ""}`}
          >
            <span>{opt.label}</span>
            {value === opt.value && <CheckIcon sx={{ fontSize: 14, color: "#2563eb" }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const ShowingDropdown: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  return (
    <>
      <button className={`um-showing-btn ${open ? "open" : ""}`} onClick={e => setAnchor(e.currentTarget)}>
        <span>{value}</span>
        <KeyboardArrowDownIcon className={`um-filter-arrow ${open ? "rotated" : ""}`} />
      </button>
      <Menu anchorEl={anchor} open={open} onClose={() => setAnchor(null)}
        PaperProps={{ className: "um-menu-paper", elevation: 0 }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        {SHOWING_OPTIONS.map(opt => (
          <MenuItem key={opt.value} disableRipple disableTouchRipple
            onClick={() => { onChange(opt.value); setAnchor(null); }}
            className={`um-menu-item ${value === opt.value ? "active" : ""}`}
          >
            <span>{opt.label}</span>
            {value === opt.value && <CheckIcon sx={{ fontSize: 14, color: "#2563eb" }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SkeletonRow: React.FC<{ index: number }> = ({ index }) => (
  <TableRow className="um-skeleton-row" style={{ animationDelay: `${index * 50}ms` }}>
    {[200, 100, 150, 100, 110, 70, 60].map((w, i) => (
      <TableCell key={i} className="um-td">
        <span className="um-shimmer" style={{ width: w }} />
      </TableCell>
    ))}
  </TableRow>
);

const UserManagement: React.FC = () => {
  const [users, setUsers]                 = useState<UserData[]>([]);
  const [loading, setLoading]             = useState(true);
  const [rowsLoading, setRowsLoading]     = useState(false);
  const [sn, setSn]                       = useState(localStorage.getItem("sideNav"));
  const data                              = useSelector((state: any) => state?.app);
  const windowWidth                       = useRef(window.innerWidth);
  const [open, setOpen]                   = useState(windowWidth.current > 1000 ? true : false);
  const navigate                          = useNavigate();
  const [search, setSearch]               = useState("");
  const [page, setPage]                   = useState(0);
  const [rowsPerPage, setRowsPerPage]     = useState(10);
  const [statusFilter, setStatusFilter]   = useState("all");
  const [roleFilter, setRoleFilter]       = useState("all");
  const [operationsMap, setOperationsMap] = useState<Map<string, OperationData[]>>(new Map());
  const searchRef                         = useRef<HTMLInputElement>(null);
  const tableWrapRef                      = useRef<HTMLDivElement>(null);

  useEffect(() => { injectStyles(); }, []);

  const handleChangePage = (newPage: number) => {
    setRowsLoading(true);
    setTimeout(() => { setPage(newPage); setRowsLoading(false); }, 150);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const getClientOperations = async (clientId: string) => {
    if (!clientId || operationsMap.has(clientId)) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/operations12/${clientId}/`);
      setOperationsMap(prev => new Map(prev).set(clientId, response.data));
    } catch (error) {
      console.error(`Error fetching operations for client ${clientId}:`, error);
    }
  };

  const getUsersData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/user/get/`, {
        headers: { 'Content-Type': 'application/json' },
      });
      const usersData = response.data;
      setUsers(usersData);
      const clientIds = usersData
        .map((u: UserData) => u.client_id)
        .filter((id: string | undefined): id is string => Boolean(id));
      const uniqueClientIds: string[] = Array.from(new Set(clientIds));
      await Promise.all(uniqueClientIds.map((clientId: string) => getClientOperations(clientId)));
    } catch (error) {
      console.error('Error fetching users data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: any) => {
    if (window.confirm('Are you sure you want to delete this User?')) {
      try {
        setLoading(true);
        await axios.delete(`${process.env.REACT_APP_BACKEND_IP}/api/user/get/${id}/`, {
          headers: { 'Content-Type': 'application/json' },
        });
        await getUsersData();
      } catch (error) {
        console.error('Error deleting user:', error);
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch { return dateString; }
  };

  const getProjectLocation = (user: UserData) => {
    if (user.client_id && user.forecast_id) {
      const operations = operationsMap.get(user.client_id);
      if (operations) {
        const matchingOperation = operations.find(
          op => op.forecast_id?.toString() === user.forecast_id?.toString()
        );
        if (matchingOperation) return matchingOperation.forecast_description;
      }
    }
    return user.operation || user.site_route || 'N/A';
  };

  const getStatus = (expectedDate: string) => {
    if (!expectedDate) return 'Inactive';
    return new Date(expectedDate) > new Date() ? 'Active' : 'Inactive';
  };

  const filteredUsers = useMemo(() => {
    return [...users]
      .filter((user) => {
        const matchesSearch = user.name.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === "all" || getStatus(user.expected_date).toLowerCase() === statusFilter;
        const matchesRole = roleFilter === "all" || user.user_type === roleFilter;
        return matchesSearch && matchesStatus && matchesRole;
      })
      .sort((a, b) => {
        const clientCompare = (a.client || '').localeCompare(b.client || '');
        if (clientCompare !== 0) return clientCompare;
        return a.id - b.id;
      });
  }, [users, search, statusFilter, roleFilter, operationsMap]);

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => { getUsersData(); }, []);
  useEffect(() => { setSn(localStorage.getItem("sideNav")); }, []);
  useEffect(() => {
    store.dispatch({ type: "TOGGLE_MENU", payload: windowWidth.current > 1000 ? true : false });
  }, []);
  useEffect(() => { setOpen(data.toggle); }, [data]);

  if (loading) {
    return (
      <div style={open ? { marginLeft: 260, width: "calc(100% - 260px)" } : {}} className="mainas">
        <WeatherLoader />
      </div>
    );
  }

  return (
    <div style={open ? { marginLeft: 260, width: "calc(100% - 260px)" } : {}} className="mainas">
      <div className="um-wrap">

        <div className="um-topbar">
          <h1 className="um-heading">User Management</h1>
          <div className="um-topbar-right">
            <div className={`um-search ${search ? "has-value" : ""}`}>
              <SearchIcon className="um-search-ico" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
              />
              {search && (
                <button className="um-search-x" onClick={() => { setSearch(""); setPage(0); searchRef.current?.focus(); }}>
                  <CloseIcon />
                </button>
              )}
            </div>

            <div className="um-showing-wrap">
              <span className="um-showing-label">Showing</span>
              <ShowingDropdown value={rowsPerPage} onChange={v => { setRowsPerPage(v); setPage(0); }} />
            </div>

            <FilterDropdown value={roleFilter} options={ROLE_OPTIONS} onChange={v => { setRoleFilter(v); setPage(0); }} />
            <FilterDropdown value={statusFilter} options={STATUS_OPTIONS} onChange={v => { setStatusFilter(v); setPage(0); }} />

            <button className="um-add-btn" onClick={() => navigate("/addnewuser")}>
              <AddIcon style={{ fontSize: 16 }} />
              Add User
            </button>
          </div>
        </div>

        <div className="um-card">
          <TableContainer
            className="um-table-wrap"
            ref={tableWrapRef}
            style={{ overflowX: "auto", scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 #f1f5f9" } as React.CSSProperties}
          >
            <Table>
              <TableHead>
                <TableRow className="um-thead">
                  <TableCell className="um-th"><div style={{ marginLeft: "30px" }}>Username</div></TableCell>
                  <TableCell className="um-th">Client</TableCell>
                  <TableCell className="um-th">Project Location</TableCell>
                  <TableCell className="um-th">Start Date</TableCell>
                  <TableCell className="um-th">Expected Date</TableCell>
                  <TableCell className="um-th">Status</TableCell>
                  <TableCell className="um-th">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsLoading
                  ? Array.from({ length: Math.min(rowsPerPage, 6) }).map((_, i) => <SkeletonRow key={i} index={i} />)
                  : paginatedUsers.length > 0
                    ? paginatedUsers.map((user, idx) => {
                        const status = getStatus(user.expected_date);
                        const avatarColor = AVATAR_PALETTE[(user.id - 1) % AVATAR_PALETTE.length];
                        return (
                          <TableRow key={user.id} className="um-trow" style={{ animationDelay: `${idx * 35}ms` }}>
                            <TableCell className="um-td">
                              <div className="um-user-cell">
                                <Avatar sx={{ width: 38, height: 38, backgroundColor: avatarColor, borderRadius: "10px", fontSize: "14px", fontWeight: 700, flexShrink: 0 }}>
                                  {user.name.charAt(0).toUpperCase()}
                                </Avatar>
                                <div className="um-user-stack">
                                  <span className="um-uname">{user.name}</span>
                                  <span className="um-uemail">{user.email_address}</span>
                                </div>
                              </div>
                            </TableCell>
                           <TableCell className="um-td" style={{ textAlign: "center" }}>{user.client}</TableCell>
                            <TableCell className="um-td um-td-loc">{getProjectLocation(user)}</TableCell>
                            <TableCell className="um-td um-td-date">{formatDate(user.start_date)}</TableCell>
                            <TableCell className="um-td um-td-date">{formatDate(user.expected_date)}</TableCell>
                            <TableCell className="um-td">
                              <span className={`um-badge um-badge--${status.toLowerCase()}`}>{status}</span>
                            </TableCell>
                            <TableCell className="um-td">
                              <div className="um-actions">
                                <IconButton size="small" className="um-act um-act--view" onClick={() => navigate(`/view/${user.id}/`)}>
                                  <VisibilityIcon />
                                </IconButton>
                                <IconButton size="small" className="um-act um-act--edit" onClick={() => navigate(`/edit/${user.id}/`)}>
                                  <EditIcon />
                                </IconButton>
                                <IconButton size="small" className="um-act um-act--del" onClick={() => handleDelete(user.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    : (
                      <TableRow>
                        <TableCell colSpan={7} className="um-empty-cell">
                          <div className="um-empty-inline">
                            <div className="um-empty-ico"><SearchIcon /></div>
                            <p className="um-empty-title">No users found</p>
                            <p className="um-empty-desc">Try adjusting your search criteria</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                }
              </TableBody>
            </Table>
          </TableContainer>

          <div className="um-pager">
            <button className="um-page-nav"
              onClick={() => handleChangePage(Math.max(page - 1, 0))}
              disabled={page === 0}
            >‹ Previous</button>

            <div className="um-page-nums">
              {[...Array(Math.min(4, totalPages))].map((_, i) => {
                const start = Math.max(0, Math.min(page - 1, totalPages - 4));
                const p = start + i;
                if (p >= totalPages) return null;
                return (
                  <button key={p} onClick={() => handleChangePage(p)} className={`um-pnum ${page === p ? "um-pnum--on" : ""}`}>
                    {String(p + 1).padStart(2, "0")}
                  </button>
                );
              })}
              {totalPages > 5 && page < totalPages - 3 && (
                <>
                  <span className="um-pdots">...</span>
                  <button onClick={() => handleChangePage(totalPages - 2)} className="um-pnum">{String(totalPages - 1).padStart(2, "0")}</button>
                  <button onClick={() => handleChangePage(totalPages - 1)} className="um-pnum">{String(totalPages).padStart(2, "0")}</button>
                </>
              )}
            </div>

            <button className="um-page-nav"
              onClick={() => handleChangePage(Math.min(page + 1, totalPages - 1))}
              disabled={page >= totalPages - 1}
            >Next ›</button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserManagement;